import { useState } from 'react'
import {
	Box,
	Typography,
	Switch
} from '@mui/material'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// Editable day row
function EditableDayRow({ dayIndex, dayName, interval, isOpen, onToggle, onChangeInterval, isMobile }) {
	const [startOpen, setStartOpen] = useState(false)
	const [endOpen, setEndOpen] = useState(false)

	const startValue = interval?.start ? dayjs(`1970-01-01T${interval.start}`) : null
	const endValue = interval?.end ? dayjs(`1970-01-01T${interval.end}`) : null

	const handleStartChange = (v) => {
		const newStart = v ? v.format('HH:mm') : ''
		if (newStart) onChangeInterval(String(dayIndex), newStart, interval?.end || '')
	}

	const handleEndChange = (v) => {
		const newEnd = v ? v.format('HH:mm') : ''
		if (newEnd) onChangeInterval(String(dayIndex), interval?.start || '', newEnd)
	}

	const timePickerSx = {
		width: '150px',
		'& .MuiOutlinedInput-root': {
			borderRadius: '6px',
			fontSize: '14px',
			fontFamily: 'Inter, sans-serif',
			height: '36px',
			'& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.3)' }
		},
		'& .MuiInputBase-input': { padding: '6px 0px 6px 12px' }
	}

	const timePickers = isOpen ? (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<TimePicker
				ampm minutesStep={5} value={startValue} open={startOpen}
				onOpen={() => setStartOpen(true)} onClose={() => setStartOpen(false)}
				onChange={handleStartChange}
				slotProps={{ textField: { size: 'small', onClick: () => setStartOpen(true), inputProps: { readOnly: true }, sx: timePickerSx } }}
			/>
			<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', color: '#2D3748', flexShrink: 0 }}>to</Typography>
			<TimePicker
				ampm minutesStep={5} value={endValue} open={endOpen}
				onOpen={() => setEndOpen(true)} onClose={() => setEndOpen(false)}
				onChange={handleEndChange}
				slotProps={{ textField: { size: 'small', onClick: () => setEndOpen(true), inputProps: { readOnly: true }, sx: timePickerSx } }}
			/>
		</LocalizationProvider>
	) : (
		<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', color: '#A0AEC0' }}>Closed</Typography>
	)

	if (isMobile) {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', py: '14px', borderBottom: dayIndex < 6 ? '1px solid #F0F0F0' : 'none' }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: '#2D3748' }}>{dayName}</Typography>
					<Switch checked={isOpen} size="small" onChange={() => onToggle(String(dayIndex))} sx={switchEnabledSx} />
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
					{timePickers}
				</Box>
			</Box>
		)
	}

	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', height: '60px', borderBottom: dayIndex < 6 ? '1px solid #F0F0F0' : 'none' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: '140px' }}>
				<Switch checked={isOpen} size="small" onChange={() => onToggle(String(dayIndex))} sx={switchEnabledSx} />
				<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', color: '#2D3748' }}>{dayName}</Typography>
			</Box>
			<Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
				{timePickers}
			</Box>
		</Box>
	)
}

const switchEnabledSx = {
	'& .MuiSwitch-switchBase.Mui-checked': { color: '#9F7AEA' },
	'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#9F7AEA' }
}

export default function StoreHoursEditor({
	inherited,
	onToggleInherited,
	weeklyHoursInherited,
	weeklyHoursDisplay,
	setWeeklyHoursDisplay,
	closedDates,
	isMobile = false
}) {
	const toggleDayOpen = (dayKey) => {
		setWeeklyHoursDisplay((prev) => {
			const currentlyOpen = (prev[dayKey] || []).length > 0
			if (currentlyOpen) {
				return { ...prev, [dayKey]: [] }
			} else {
				return { ...prev, [dayKey]: [{ start: '09:00', end: '17:00' }] }
			}
		})
	}

	const changeInterval = (dayKey, start, end) => {
		setWeeklyHoursDisplay((prev) => ({
			...prev,
			[dayKey]: [{ start, end }]
		}))
	}

	// Which hours to show
	const displayHours = inherited ? weeklyHoursInherited : weeklyHoursDisplay

	return (
		<Box>
			{/* Inherit toggle card */}
			<Box
				sx={{
					bgcolor: '#F7F6F3',
					border: '1px solid rgba(0, 0, 0, 0.2)',
					borderRadius: '12px',
					p: isMobile ? '12px 16px' : '16px 24px',
					display: 'flex',
					alignItems: 'center',
					gap: '8px',
					mb: '18px'
				}}
			>
				<Box sx={{ flex: 1 }}>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '1.5em', color: '#2D3748' }}>
						Inherit hours & closed dates from Store Settings
					</Typography>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '12px', lineHeight: '1.21em', color: '#A0AEC0' }}>
						{inherited ? 'Hours are synced from Settings.' : 'Custom hours for your landing page.'}
					</Typography>
				</Box>
				<Switch
					checked={inherited}
					onChange={(e) => onToggleInherited(e.target.checked)}
					sx={{
						flexShrink: 0,
						...switchEnabledSx
					}}
				/>
			</Box>

			{/* Weekly Hours - only shown when NOT inherited */}
			{!inherited && (
				<Box
					sx={{
						bgcolor: '#FFFFFF',
						borderRadius: '12px',
						border: '1px solid rgba(0, 0, 0, 0.1)',
						px: isMobile ? '16px' : '24px',
						py: '4px'
					}}
				>
					{DAY_NAMES.map((name, idx) => {
						const intervals = displayHours[String(idx)] || []
						const interval = intervals[0] || null
						return (
							<EditableDayRow
								key={idx}
								dayIndex={idx}
								dayName={name}
								interval={interval}
								isOpen={intervals.length > 0}
								onToggle={toggleDayOpen}
								onChangeInterval={changeInterval}
								isMobile={isMobile}
							/>
						)
					})}
				</Box>
			)}
		</Box>
	)
}
